import { SessionService } from './session-service.service';
import { NgxMaskModule } from 'ngx-mask';
import { AngularIbanModule } from 'angular-iban';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatStepperModule } from '@angular/material/stepper';
import { CapitalizeDirective } from './components/directives/CapitalizeDirective';
import { UppercaseDirective } from './components/directives/UppercaseDirective';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AreaInputComponent } from './components/area-input/area-input';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CheckboxInputComponent } from './components/checkbox-input/checkbox-input';
import { CheckboxWithTextComponent } from './components/checkbox-with-text/checkbox-with-text';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, registerLocaleData } from '@angular/common';
import { DatePipe } from '@angular/common';
import { EditorInputComponent } from './components/editor-input/editor-input';
import { EditorModule } from '@tinymce/tinymce-angular';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { IntermediaryComponent } from './components/intermediary-component/intermediary-component';
import { KeycloakAngularModule } from 'keycloak-angular';
import { RadioInputComponent } from './components/radio-input/radio-input';
import { CommencementDateInputComponent } from './components/commencementDate-input/commencementDate-input';
import { SpinnerComponent } from './components/spinner-component/spinner.component';
import { TextInputComponent } from './components/text-input/text-input';
import { ToggleInputComponent } from './components/toggle-input/toggle-input';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClientModule, HttpClient } from '@angular/common/http';

import { ConfirmationDialogComponent } from './dialogs/confirmation-dialog/confirmation-dialog';

import { ApplicationPageComponent } from './pages/ORV/application-page/application-page.component';
import { ApplicationPageGGComponent } from './pages/GG/application-page/application-page.component';
import { ConfigurationPageOrvComponent } from './pages/configuration-page/configuration-page-orv/configuration-page-orv.component';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { ConfigurationPageComponent } from './pages/configuration-page/configuration-page.component';
import { ConfigurationPageGgComponent } from './pages/configuration-page/configuration-page-gg/configuration-page-gg.component';

import { CalculationPageComponent } from './pages/ORV/calculation-page/calculation-page.component';
import { CalculationPageGGComponent } from './pages/GG/calculation-page/calculation-page.component';
import { ApplicationInsuredComponent } from './pages/application-insured/application-insured.component';
import { ApplicationPolicyHolderComponent } from './pages/application-policy-holder/application-policy-holder.component';
import { ApplicationPremiumPayerComponent } from './pages/application-premium-payer/application-premium-payer.component';
import { ApplicationBeneficiariesComponent } from './pages/application-beneficiaries/application-beneficiaries.component';
import { ApplicationOtherInsurancesComponent } from './pages/application-other-insurances/application-other-insurances.component';
import { ApplicationPolicyHolderTwoComponent } from './pages/application-policy-holder-two/application-policy-holder-two.component';
import { CalculationInsuredComponent } from './pages/calculation-insured/calculation-insured.component';
import { DateInputComponent } from './components/date-input/date-input';
import { ApplicationCustomBeneficiaryComponent } from './pages/application-custom-beneficiary/application-custom-beneficiary.component';
import { ApplicationPledgeeComponent } from './pages/application-pledgee/application-pledgee.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { PendingChangesGuard } from './services/PendingChangesGuard';
import { CopyClipboardDirective } from './copy-clipboard.directive';
import { ConfigSettingsNavigationStepDirective } from './configuration/configSettingsNavigationStep.directive';
import { ApplicationSummaryComponent } from './pages/application-summary/application-summary.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ApplicationSecondInsuredComponent } from './pages/application-second-insured/application-second-insured.component';
import { AutocompleteInputComponent } from './components/autocomplete-input/autocomplete-input.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ApplicationThankYouComponent } from './pages/application-thank-you/application-thank-you.component';
import { ApplicationStartIdentificationComponent } from './pages/application-start-identification/application-start-identification.component';
import { IdinVerificationErrorPageComponent } from './pages/idin-verification-error-page/idin-verification-error-page.component';
import { IdinVerificationCancelledPageComponent } from './pages/idin-verification-cancelled-page/idin-verification-cancelled-page.component';
import { PremiumsOverviewComponent } from './components/premiums-overview/premiums-overview.component';
import { BackButtonComponent } from './components/back-button/back-button';
import { ApplicationInsuranceComponent } from './pages/application-insurance/application-insurance.component';
import { NavigationComponent } from './components/navigation-component/navigation-component';
import { PremiumBlockComponent } from './pages/application-premium-block/application-premium-block.component';
import { ProductInfoPageComponent } from './pages/GG/product-info-page/product-info-page.component';
import { SummaryPageComponent } from './pages/GG/summary-page/summary-page.component';
import { PersonalDetailsPageGGComponent } from './pages/GG/personal-details-page/personal-details-page.component';
import { KnowledgeTestComponent } from './pages/GG/knowledge-test/knowledge-test.component';
import { KnowledgeTestFormService } from './pages/GG/knowledge-test/knowledge-test-form.service';
import { KnowledgeTestSelectComponent } from './components/knowledge-test-select-component/knowledge-test-select.component';
import { KnowledgeTestEmailOfferFormComponent } from './pages/GG/knowledge-test/knowledge-test-email-offer-form/knowledge-test-email-offer-form.component';

import localeNl from '@angular/common/locales/nl';
import { AvailablePremiumsPipe } from './available-premiums.pipe';
import { SelectBoxComponent } from './components/select-box/select-box.component';
import { IdinVerificationIssuerResponsePageComponent } from './pages/idin-verification-issuer-response-page/idin-verification-issuer-response-page.component';
import { IdinVerificationSuccessPageComponent } from './pages/idin-verification-success-page/idin-verification-success-page.component';
import { IdinVerificationFailedPageComponent } from './pages/idin-verification-failed-page/idin-verification-failed-page.component';
import { IdinDemoPageComponent } from './pages/idin-demo-page/idin-demo-page.component';
import { NotificationBlockComponent } from './components/notification-block/notification-block.component';
import { CalculationPageResolverComponent } from './components/routing-resolvers/calculation-page-resolver/calculation-page-resolver';
import { ApplicationPageResolverComponent } from './components/routing-resolvers/application-page-resolver/application-page-resolver';
import { SessionOrvService } from './services/session-orv.service';
import { SessionGgService } from './services/session-gg.service';
import { TemplateService } from './services/TemplateService';
import { InitialsComponent } from './components/initials-component/initials-component';
import { CalculatorModule } from './calculator/calculator.module';

import { NgClickOutsideModule } from 'ng-click-outside2';
import { CommonDirectivesModule } from './common-directives/common-directives.module';
import { CalculationGgService } from './services/calculation-gg.service';
import { ApplicationIdinWrapperComponent } from './pages/appliaction-idin-wrapper/application-idin-wrapper';
import { ApplicationRequesterDateAdapter } from './services/DateAdapter';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

registerLocaleData(localeNl);

@NgModule({
  declarations: [
    CapitalizeDirective,
    UppercaseDirective,
    InitialsComponent,
    AppComponent,
    AreaInputComponent,
    CheckboxInputComponent,
    CheckboxWithTextComponent,
    EditorInputComponent,
    IntermediaryComponent,
    RadioInputComponent,
    DateInputComponent,
    ToggleInputComponent,
    SpinnerComponent,
    TextInputComponent,
    AutocompleteInputComponent,
    CommencementDateInputComponent,
    ApplicationStartIdentificationComponent,

    CalculationInsuredComponent,
    ConfirmationDialogComponent,
    ApplicationPageComponent,
    ApplicationPageGGComponent,
    CalculationPageComponent,
    CalculationPageGGComponent,
    ConfigurationPageOrvComponent,
    ConfigurationPageGgComponent,
    ConfigurationPageComponent,
    PageNotFoundComponent,
    ApplicationInsuredComponent,
    ApplicationPolicyHolderComponent,
    ApplicationPremiumPayerComponent,
    ApplicationBeneficiariesComponent,
    ApplicationOtherInsurancesComponent,
    ApplicationPolicyHolderTwoComponent,
    ApplicationCustomBeneficiaryComponent,
    ApplicationPledgeeComponent,
    CopyClipboardDirective,
    ConfigSettingsNavigationStepDirective,
    ApplicationSummaryComponent,
    ApplicationSecondInsuredComponent,
    ApplicationThankYouComponent,
    ApplicationStartIdentificationComponent,
    IdinVerificationErrorPageComponent,
    IdinVerificationCancelledPageComponent,
    PremiumsOverviewComponent,
    BackButtonComponent,
    ApplicationInsuranceComponent,
    AvailablePremiumsPipe,
    NavigationComponent,
    SelectBoxComponent,
    IdinVerificationIssuerResponsePageComponent,
    IdinVerificationSuccessPageComponent,
    IdinVerificationFailedPageComponent,
    IdinDemoPageComponent,
    NotificationBlockComponent,
    CalculationPageResolverComponent,
    ApplicationPageResolverComponent,
    PremiumBlockComponent,
    ProductInfoPageComponent,
    SummaryPageComponent,
    PersonalDetailsPageGGComponent,
    KnowledgeTestComponent,
    KnowledgeTestSelectComponent,
    KnowledgeTestEmailOfferFormComponent,
    ApplicationIdinWrapperComponent,
  ],
  imports: [
    MatAutocompleteModule,
    AngularIbanModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    MatButtonModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatRadioModule,
    MatToolbarModule,
    MatListModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatGridListModule,
    MatTabsModule,
    MatStepperModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSidenavModule,
    NgxMaskModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    AppRoutingModule,
    KeycloakAngularModule,
    EditorModule,
    ColorPickerModule,
    MatButtonToggleModule,
    NgClickOutsideModule,
    CalculatorModule,
    CommonDirectivesModule,
  ],
  providers: [
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    DatePipe,
    {
      provide: MAT_DATE_LOCALE,
      useValue: getBrowserCulture(),
    },
    {
      provide: DateAdapter,
      useClass: ApplicationRequesterDateAdapter,
    },
    PendingChangesGuard,
    AvailablePremiumsPipe,
    SessionOrvService,
    SessionGgService,
    SessionService,
    CalculationGgService,
    TemplateService,
    KnowledgeTestFormService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function getBrowserCulture(): string {
  const nlLanguageCode = 'nl';
  const enCulture = 'en-GB';
  const nlCulture = 'nl-NL';

  const browserLanguage = navigator.language;
  return browserLanguage.startsWith(nlLanguageCode) ? nlCulture : enCulture;
}
